import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import Text from '@/components/Typography/Text';
import FlowField from '@/components/Landing/FlowField';
import LandingClosing from '@/components/Landing/LandingClosing';
import Reveal from '@/components/Landing/Reveal';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import { RuledGrid } from '@/components/Landing/MarketingKit';
import {
  Accent,
  BleedRule,
  CtaRow,
  DisplayHeading,
  GhostCta,
  Kicker,
  Lede,
  Marquee,
  PrimaryCta,
  Section,
  Split,
  useEntrance,
  useLanding,
} from '@/components/Landing/LandingKit';
import {
  ACCENT,
  LAYOUT,
  fluid,
  isWeb,
  labelType,
} from '@/components/Landing/landingTokens';
import { isDarkTheme } from '@/constants/Colors';
import { COMPETITOR_ALTERNATIVES } from '@/constants/competitorAlternatives';
import { FREE_USER_STARTING_CREDITS_DISPLAY, GAB_AI_SOFTWARE_ID } from '@/constants/config';
import { useTheme } from '@/context/ThemeContext';

const SITE = 'https://gab.ai';
const CANONICAL = '/alternatives';

const TITLE = 'AI Chat Alternatives — Compared Side by Side';
const DESCRIPTION = 'Compare Gab AI with ChatGPT, Poe, Monica, ChatHub, TypingMind, t3.chat, Galaxy AI, and OpenRouter — models, images, video, and music on one free account.';
const KEYWORDS = 'AI chat alternatives, ChatGPT alternatives, AI chatbot alternatives, alternatives to ChatGPT, best AI chat app, compare AI chat apps, AI assistant comparison';

/** What every comparison ends up arguing, stated once. */
const SHARED = [
  {
    key: 'models',
    icon: 'layer-group',
    title: 'One picker, every model',
    copy: 'GPT, Claude, Gemini, and Arya per message — not one company’s lineup, and not a directory of wrappers around it.',
    href: '/models',
    cta: 'See every model',
  },
  {
    key: 'scope',
    icon: 'wand-magic-sparkles',
    title: 'More than chat',
    copy: 'Images, video, music, voice, documents, and file analysis in the same conversation, spending from the same balance.',
    href: '/features',
    cta: 'See all features',
  },
  {
    key: 'privacy',
    icon: 'user-secret',
    title: 'Private by default',
    copy: 'We never train on your conversations, and third-party requests are anonymized so those providers never learn who is asking.',
    href: '/docs/privacy',
    cta: 'Read the privacy docs',
  },
  {
    key: 'arya',
    icon: 'comments',
    title: 'Answers, not hedges',
    copy: 'Arya is our own model. It answers political, cultural, and religious questions directly instead of deflecting.',
    href: '/right-wing-ai',
    cta: 'Meet Arya',
  },
];

const FAQS = [
  {
    q: 'What is the best ChatGPT alternative?',
    a: 'It depends on what you used it for. If you only ever want models from one company, staying put is reasonable. If you want to switch between GPT, Claude, Gemini, and Arya per message, generate images and video in the same thread, keep your conversations out of training data, and get direct answers on subjects other assistants avoid, that is the gap Gab AI fills.',
  },
  {
    q: 'Is there a free alternative to these apps?',
    a: `Yes. Gab AI is free to start: a new account comes with ${FREE_USER_STARTING_CREDITS_DISPLAY} credits and needs no credit card, and those credits work across every model and every medium — chat, images, video, and music included.`,
  },
  {
    q: 'Can I keep using GPT and Claude if I switch?',
    a: 'Yes. GPT, Claude, and Gemini are all in the picker alongside Arya. Switching to Gab AI means adding models rather than giving one up, and requests to those providers are anonymized so they never learn who is asking.',
  },
  {
    q: 'Do I lose my chat history?',
    a: 'No. Export your data from ChatGPT, Claude, or Grok and upload the file once. Threads are matched and de-duplicated, and each arrives with its title and timestamp intact.',
  },
];

/* ------------------------------------------------------------------ */

/**
 * One comparison, linking to the full page.
 *
 * The whole card is not a link: `GhostCta` is the anchor, so the crawlable
 * anchor text stays the target's primary keyword rather than the card's prose.
 */
function ComparisonCard({ page }) {
  const { c, isXS, isCompact, display } = useLanding();
  const h3 = display({ min: 21, max: 27, leading: 1.14, tracking: -0.03 });

  return (
    <View
      style={{
        flexGrow: 1,
        flexBasis: isCompact ? '100%' : '46%',
        minWidth: 0,
        borderRadius: 22,
        borderWidth: 1,
        borderColor: c.rule,
        backgroundColor: c.cardSolid,
        padding: isXS ? 22 : 28,
        gap: isXS ? 18 : 22,
        justifyContent: 'space-between',
      }}
    >
      <View style={{ gap: 12 }}>
        <Text weight="bold" style={{ color: c.faint, ...labelType(9.5, 0.2) }}>
          {page.compareOtherLabel}
        </Text>

        <DisplayHeading tag="h3" mask={false} type={h3} lines={[[page.breadcrumbLabel]]} />

        <Text style={{ color: c.muted, fontSize: 14, lineHeight: 22, maxWidth: 440 }}>
          {page.hubSummary}
        </Text>
      </View>

      <GhostCta
        label={`Compare with ${page.competitor}`}
        href={page.slug}
        style={{ height: 34 }}
      />
    </View>
  );
}

/* ------------------------------------------------------------------ */

export default function AlternativesPage() {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const insets = useSafeAreaInsets();
  const { c, isXS, isCompact, width, pad, display } = useLanding();
  const entered = useEntrance();

  useLandingStyles();

  const headline = display({ min: 38, max: 74, leading: 0.98, tracking: -0.042 });
  const h2 = display({ min: 30, max: 52, leading: 1.02, tracking: -0.038 });

  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${SITE}${CANONICAL}#page`,
        url: `${SITE}${CANONICAL}`,
        name: 'AI chat alternatives',
        description: DESCRIPTION,
        isPartOf: { '@id': GAB_AI_SOFTWARE_ID },
        breadcrumb: { '@id': `${SITE}${CANONICAL}#breadcrumb` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE}${CANONICAL}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Gab AI', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'AI chat alternatives', item: `${SITE}${CANONICAL}` },
        ],
      },
      {
        '@type': 'ItemList',
        '@id': `${SITE}${CANONICAL}#comparisons`,
        name: 'Gab AI compared with other AI chat apps',
        itemListElement: COMPETITOR_ALTERNATIVES.map((page, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: page.breadcrumbLabel,
          url: `${SITE}${page.slug}`,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE}${CANONICAL}#faq`,
        mainEntity: FAQS.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  }), []);

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title={TITLE}
        description={DESCRIPTION}
        canonical={CANONICAL}
        keywords={KEYWORDS}
        structuredData={structuredData}
      />

      <ScrollView
        {...(isWeb ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole={safeAccessibilityRole('main')}
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        {/* ============ MASTHEAD ============ */}
        <View
          accessibilityRole={isWeb ? 'region' : undefined}
          accessibilityLabel="AI chat alternatives compared"
          style={{
            width: '100%',
            maxWidth: LAYOUT.max,
            paddingHorizontal: pad,
            paddingTop: (isXS ? insets.top : 0) + fluid(width, 104, 152),
            paddingBottom: fluid(width, 40, 60),
          }}
        >
          <View
            style={isWeb ? {
              opacity: entered ? 1 : 0,
              transition: 'opacity 700ms ease 120ms',
            } : null}
          >
            <Kicker>Compare</Kicker>
          </View>

          <DisplayHeading
            tag="h1"
            entered={entered}
            type={headline}
            style={{ marginTop: fluid(width, 22, 32) }}
            lines={[
              ['AI chat alternatives,'],
              [
                <Accent key="a" size={headline.fontSize} leading={headline.lineHeight}>compared</Accent>,
                '.',
              ],
            ]}
          />

          <View style={{ height: 1, backgroundColor: c.rule, marginTop: fluid(width, 30, 48) }} />

          <View
            style={{
              flexDirection: isCompact ? 'column' : 'row',
              gap: isCompact ? 30 : 56,
              alignItems: isCompact ? 'stretch' : 'flex-start',
              paddingTop: fluid(width, 26, 40),
              ...(isWeb ? {
                opacity: entered ? 1 : 0,
                transform: [{ translateY: entered ? 0 : 18 }],
                transition: 'opacity 800ms ease 420ms, transform 800ms ease 420ms',
              } : {}),
            }}
          >
            <View style={{ flex: isCompact ? undefined : 1.15, minWidth: 0 }}>
              <Lede max={480}>
                Every comparison below answers the same question from a different starting
                point: what you keep, what you gain, and what it costs to move. Read the one
                for the app you are actually leaving.
              </Lede>
            </View>

            <View style={{ flex: isCompact ? undefined : 1, minWidth: 0, gap: 18 }}>
              <CtaRow>
                <PrimaryCta label="Start free" full={isXS} />
                <GhostCta label="Import your chats" href="/switch" />
              </CtaRow>
              <Text style={{ color: c.faint, fontSize: 12.5, letterSpacing: 0.1 }}>
                {`${FREE_USER_STARTING_CREDITS_DISPLAY} credits to start · No credit card required`}
              </Text>
            </View>
          </View>
        </View>

        <BleedRule />
        <Marquee items={COMPETITOR_ALTERNATIVES.map((page) => page.competitor)} />
        <BleedRule />

        {/* ============ THE COMPARISONS ============ */}
        <Section label="Gab AI compared with other AI chat apps">
          <Reveal>
            <Kicker>The comparisons</Kicker>
            <DisplayHeading
              tag="h2"
              mask={false}
              type={h2}
              style={{ marginTop: 22 }}
              lines={[['Whichever one'], ['you are leaving.']]}
            />
            <Lede max={540} style={{ marginTop: 20 }}>
              Each page compares Gab AI against a category rather than a rival&rsquo;s current
              feature sheet — those change weekly, and a comparison written against one goes
              stale on its own.
            </Lede>
          </Reveal>

          <Reveal delay={100}>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: isXS ? 12 : 16,
                marginTop: fluid(width, 36, 56),
              }}
            >
              {COMPETITOR_ALTERNATIVES.map((page) => (
                <ComparisonCard key={page.slug} page={page} />
              ))}
            </View>
          </Reveal>
        </Section>

        <BleedRule />

        {/* ============ WHAT THEY SHARE ============ */}
        <Section label="What every comparison has in common">
          <Reveal>
            <Kicker>In common</Kicker>
            <DisplayHeading
              tag="h2"
              mask={false}
              type={h2}
              style={{ marginTop: 22 }}
              lines={[['Four answers,'], ['eight questions.']]}
            />
          </Reveal>

          <Reveal delay={100}>
            <RuledGrid
              items={SHARED}
              keyOf={(item) => item.key}
              edges="both"
              style={{ marginTop: fluid(width, 34, 48) }}
              renderItem={(item) => (
                <>
                  <View
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 11,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: c.accentSoft,
                    }}
                  >
                    <Icon name={item.icon} size={14} color={ACCENT} solid />
                  </View>
                  <Text
                    tag="h3"
                    weight="bold"
                    style={{ color: c.ink, fontSize: 16.5, letterSpacing: -0.2, lineHeight: 24 }}
                  >
                    {item.title}
                  </Text>
                  <Text style={{ color: c.muted, fontSize: 13.5, lineHeight: 21 }}>
                    {item.copy}
                  </Text>
                  <GhostCta label={item.cta} href={item.href} style={{ height: 34 }} />
                </>
              )}
            />
          </Reveal>
        </Section>

        {/* ============ FAQ ============ */}
        <Section label="AI chat alternative questions">
          <Split ratio={[1, 1.5]} align="flex-start">
            <Reveal>
              <Kicker>Answers</Kicker>
              <DisplayHeading
                tag="h2"
                mask={false}
                type={h2}
                style={{ marginTop: 22 }}
                lines={[['Before you'], ['switch.']]}
              />
              <Lede size="sm" max={340} style={{ marginTop: 20 }}>
                Compare the plans, read how importing works, or just start a chat and see
                whether the answers are better.
              </Lede>
              <GhostCta label="Compare plans" href="/pricing" style={{ marginTop: 12 }} />
              <GhostCta label="Try Gab AI free" href="/free-ai-chat" style={{ marginTop: 10 }} />
            </Reveal>

            <Reveal delay={100}>
              <View style={{ borderTopWidth: 1, borderTopColor: c.rule }}>
                {FAQS.map((item) => (
                  <View
                    key={item.q}
                    style={{
                      paddingVertical: fluid(width, 18, 24),
                      borderBottomWidth: 1,
                      borderBottomColor: c.rule,
                      gap: 8,
                    }}
                  >
                    <Text
                      tag="h3"
                      weight="bold"
                      style={{ color: c.ink, fontSize: 16.5, letterSpacing: -0.2, lineHeight: 24 }}
                    >
                      {item.q}
                    </Text>
                    <Text tag="p" style={{ color: c.muted, fontSize: 14, lineHeight: 22, maxWidth: 620 }}>
                      {item.a}
                    </Text>
                  </View>
                ))}
              </View>
            </Reveal>
          </Split>
        </Section>

        <LandingClosing />
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
